import { useTranslation } from "react-i18next";
import { Clock as ClockIcon } from "lucide-react";
import type { PlatformTypeName } from "@multi-chat/shared";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import {
  deriveReplyWindow,
  remainingReplyWindowParts,
  replyWindowTone,
  type ReplyWindowTone,
} from "../conversation-header-timing.js";
import { useNowMs } from "../use-now-ms.js";

const CHIP_TONE_CLASS: Record<ReplyWindowTone, string> = {
  open: "bg-inbox-pill text-[#3d534d]",
  warn: "bg-inbox-calm-bg text-inbox-draft",
  closed: "bg-inbox-calm-bg text-inbox-calm",
};

/** Compact reply-window state for the thread header, with the provider's rule as a tooltip. */
export function ConversationReplyWindowChip({
  platformType,
  customerServiceWindowExpiresAt,
  lastInboundCustomerMessageAt,
  metaWindowClosed,
  withSeparator = false,
  className,
}: {
  platformType: PlatformTypeName;
  customerServiceWindowExpiresAt: string | null;
  lastInboundCustomerMessageAt: string | null;
  metaWindowClosed: boolean;
  /** Leading "·" after the identity; omitted with the chip when there is no window state. */
  withSeparator?: boolean;
  className?: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const nowMs = useNowMs();
  const window = deriveReplyWindow({
    platformType,
    customerServiceWindowExpiresAt,
    lastInboundCustomerMessageAt,
    nowMs,
    metaWindowClosed,
  });

  if (window.kind === "NONE") return null;

  const tone = replyWindowTone(window);
  let label: string;
  let templateRequired = false;
  switch (window.kind) {
    case "CLOSED":
      label = t("inbox.header.replyWindowClosed");
      templateRequired = window.templateRequired;
      break;
    case "OPEN": {
      const parts = remainingReplyWindowParts(window.remainingMs);
      if (parts.unit === "hour") {
        label =
          parts.count === 1
            ? t("inbox.header.hourLeftToReply")
            : t("inbox.header.hoursLeftToReply", { count: parts.count });
      } else {
        label =
          parts.count === 1
            ? t("inbox.header.minuteLeftToReply")
            : t("inbox.header.minutesLeftToReply", { count: parts.count });
      }
      break;
    }
    default: {
      const _exhaustive: never = window;
      return _exhaustive;
    }
  }

  const tip =
    platformType === "WHATSAPP"
      ? t("inbox.header.windowTipWhatsApp")
      : t("inbox.header.windowTipMeta");

  return (
    <>
      {withSeparator ? (
        <span className="shrink-0 text-[#b3c1bd]" aria-hidden="true">
          ·
        </span>
      ) : null}
      <SimpleTooltip content={tip}>
        <span
          tabIndex={0}
          data-testid="thread-reply-window"
          data-window-state={tone}
          className={cn(
            "inline-flex h-5 min-w-0 shrink-0 items-center gap-1 rounded-full px-[7px] text-[0.71875rem] font-[550] whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
            CHIP_TONE_CLASS[tone],
            className,
          )}
        >
          <ClockIcon size={12} className="shrink-0" aria-hidden="true" />
          <span className="truncate">
            {label}
            {templateRequired ? (
              <>
                {" · "}
                <span className="font-bold text-inbox-draft">{t("inbox.header.templateRequired")}</span>
              </>
            ) : null}
          </span>
        </span>
      </SimpleTooltip>
    </>
  );
}
